import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { AppConfigService } from '../../services/app-config.service';
import {timer} from "rxjs";
import {filter, tap} from "rxjs/operators";

@Component({
  selector: 'app-footer-with-environment-informations',
  templateUrl: './footer-with-environment-informations.component.html',
  styleUrls: ['./footer-with-environment-informations.component.scss'],
  animations: [
    trigger('slide', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate('180ms ease-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('150ms ease-in', style({ height: 0, opacity: 0 }))
      ])
    ])
  ]
})
export class FooterWithEnvironmentInformationsComponent implements OnInit {
  environmentName = '';
  modulesVersion: Record<string, string> = {};
  version = '';
  showAll = false;
  justCopied = false;

  private _allVersions: Array<{ key: string; value: string }> = [];
  private _markdownTable = '';

  constructor(public appConfigService: AppConfigService) {}

  ngOnInit(): void {
    this.appConfigService.getModulesVersion()
      .pipe(
        filter(apiVal => apiVal.success),
        tap(apiVal => {
          this.modulesVersion = apiVal.item ?? {};
          this.version = this.modulesVersion['web'] ?? '';
          this.environmentName = this.appConfigService.webTitle;
          this._allVersions = this.computeAllVersions();
          this._markdownTable = this.computeMarkdownTable();
        })
      )
      .subscribe();
  }

  trackByKey(_index: number, item: { key: string; value: string }): string {
    return item.key;
  }

  toggleVersions(ev?: MouseEvent): void {
    ev?.stopPropagation();
    this.showAll = !this.showAll;
  }

  @HostListener('document:keydown.escape')
  closeOnEsc() { this.showAll = false; }

  get allVersions(): Array<{ key: string; value: string }> {
    return this._allVersions;
  }

  private computeAllVersions(): Array<{ key: string; value: string }> {
    const entries = Object.entries(this.modulesVersion ?? {});
    const keyRank = (k: string): [number, string] => {
      const s = (k || '').toLowerCase();
      if (s === 'web') return [0, s];
      if (s === 'hub') return [1, s];
      if (s === 'archive') return [2, s];
      if (s.startsWith('gateway-')) return [4, s]; // last group
      return [3, s]; // middle group (everything else)
    };

    entries.sort((a, b) => {
      const [ra, sa] = keyRank(a[0]);
      const [rb, sb] = keyRank(b[0]);
      if (ra !== rb) return ra - rb;
      return sa.localeCompare(sb);
    });

    return entries.map(([key, value]) => ({ key, value }));
  }

  /** Markdown content to copy — horizontal single-row table */
  get markdownTable(): string {
    return this._markdownTable;
  }

  private computeMarkdownTable(): string {
    const mv = this.modulesVersion ?? {};

    // Fixed columns first
    const fixedStart: Array<{ label: string; key: string }> = [
      { label: 'WEB', key: 'web' },
      { label: 'HUB', key: 'hub' },
      { label: 'ARCHIVE', key: 'archive' },
    ];

    // All gateway-* modules, sorted A→Z by key
    const gateways = Object.keys(mv)
      .filter(k => k.toLowerCase().startsWith('gateway-'))
      .sort((a, b) => a.localeCompare(b))
      .map(k => ({
        key: k,
        // "gateway-ice-pof" -> "GW ICE-POF"
        label: 'GW ' + k.slice('gateway-'.length).toUpperCase()
      }));


    // Final column sequence
    const cols = [...fixedStart, ...gateways];

    // Build Markdown table
    const header = `| ${cols.map(c => c.label).join(' | ')} |`;
    const sep    = `| ${cols.map(() => '---').join(' | ')} |`;
    const data   = `| ${cols.map(c => mv[c.key] ?? '').join(' | ')} |`;

    return [
      `**Environment:** ${this.environmentName || ''}`,
      '',
      header,
      sep,
      data
    ].join('\n');
  }


  /** Called by CDK when copy succeeds/fails */
  onCopied(success: boolean) {
    this.justCopied = success;
    if (success) {
      // Optionally close after copy:
      this.showAll = false;
      // Small transient UI hint
      timer(1500)
        .pipe(tap(() => (this.justCopied = false)))
        .subscribe();
    }
  }
}
